import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, FileArrowDown } from "@phosphor-icons/react";

const CATEGORIES = ["Professional", "Promotional", "Research", "Operational", "Staffing", "Premises", "Other"];
const CURRENCIES = ["EUR", "GBP", "USD"];

export const downloadCsv = async (path, filename) => {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const eligBadge = (i) => {
  if (i.eligible) return <span className="badge-pass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">Eligible</span>;
  return <span className="badge-warn px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">Not eligible</span>;
};

export default function PreTradingTab({ onChange }) {
  const [data, setData] = useState({ items: [], eligible_total: 0, ineligible_total: 0, capital_count: 0, window_start: "", commencement_date: "" });
  const [form, setForm] = useState({ description: "", category: "Professional", amount: "", currency: "EUR", expense_date: "", wholly_exclusively: true, revenue_nature: true, notes: "" });

  const load = useCallback(async () => { const r = await api.get("/compliance/pretrading"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/compliance/pretrading", { ...form, amount: Number(form.amount) });
      setForm({ description: "", category: "Professional", amount: "", currency: "EUR", expense_date: "", wholly_exclusively: true, revenue_nature: true, notes: "" });
      load(); onChange && onChange();
    } catch (err) { alert(err?.response?.data?.detail || err.message); }
  };

  const del = async (id) => { await api.delete(`/compliance/pretrading/${id}`); load(); onChange && onChange(); };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <div className="eyebrow mb-4">Log a pre-trading expense</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2"><label>Description</label><input required data-testid="pt-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Accountancy fees" /></div>
          <div><label>Category</label><select data-testid="pt-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label>Amount</label><input required type="number" step="0.01" data-testid="pt-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
          <div><label>Currency</label><select data-testid="pt-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label>Date incurred</label><input required type="date" data-testid="pt-date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></div>
          <button type="submit" data-testid="pt-add" className="btn-gold"><Plus weight="bold" size={14} /> Add</button>
          {form.currency !== "EUR" && <div className="md:col-span-6 text-[11px] text-white/45">Non-EUR amounts are converted to EUR at the ECB rate on the date incurred and stored for reporting.</div>}
          <label className="flex items-center gap-2 text-xs text-white/70 md:col-span-3"><input type="checkbox" className="w-auto" data-testid="pt-we" checked={form.wholly_exclusively} onChange={(e) => setForm({ ...form, wholly_exclusively: e.target.checked })} /> Wholly &amp; exclusively for the trade</label>
          <label className="flex items-center gap-2 text-xs text-white/70 md:col-span-3"><input type="checkbox" className="w-auto" data-testid="pt-revenue" checked={form.revenue_nature} onChange={(e) => setForm({ ...form, revenue_nature: e.target.checked })} /> Revenue nature (untick if capital — claim via Capital Allowances)</label>
        </form>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-6 text-sm">
          <div><span className="text-white/50">Eligible total:</span> <span className="mono text-[#00A86B]" data-testid="pt-eligible-total">€{Number(data.eligible_total).toLocaleString()}</span></div>
          <div><span className="text-white/50">Capital items flagged:</span> <span className="mono text-[#FFB020]">{data.capital_count}</span></div>
        </div>
        <button onClick={() => downloadCsv("/compliance/pretrading.csv", "pre-trading-expenses.csv")} data-testid="pt-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date</th><th>Description</th><th>Category</th><th className="text-right">Amount</th><th>3-yr</th><th>W&amp;E</th><th>Nature</th><th>Eligibility</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="9" className="text-center text-white/40 py-8">No pre-trading expenses logged. Set your commencement date in Company Setup to enable the 3-year check.</td></tr>
            ) : data.items.map((i) => (
              <tr key={i.expense_id} data-testid={`pt-row-${i.expense_id}`}>
                <td className="mono text-xs">{i.expense_date}</td>
                <td>{i.description}</td>
                <td className="text-white/70">{i.category}</td>
                <td className="num">€{Number(i.amount).toFixed(2)}{i.currency && i.currency !== "EUR" ? <div className="text-[10px] text-white/40">{Number(i.original_amount).toFixed(2)} {i.currency}</div> : null}</td>
                <td>{i.within_three_years === null ? "—" : (i.within_three_years ? "✓" : "✗")}</td>
                <td>{i.wholly_exclusively ? "✓" : "✗"}</td>
                <td className={i.is_capital ? "text-[#E63946]" : "text-white/70"}>{i.is_capital ? "Capital" : "Revenue"}</td>
                <td>{eligBadge(i)}</td>
                <td><button onClick={() => del(i.expense_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`pt-del-${i.expense_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
